import { defaultBusiness as NAP } from "@/lib/site-config";
import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { Phone, ArrowRight, Star, MapPin, MessageCircle } from "lucide-react";
import { services, reviews, areas, getBusinessConfig } from "@/lib/site-config";
import { ServiceCard } from "@/components/ServiceCard";
import { ReviewCard } from "@/components/ReviewCard";
import { CTABlock } from "@/components/CTABlock";
import { FAQAccordion, faqSchema } from "@/components/FAQAccordion";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { ReassuranceBanner } from "@/components/ReassuranceBanner";
import { ServicePicker } from "@/components/ServicePicker";
import { ProofStrip } from "@/components/ProofStrip";
import { WhatToExpect } from "@/components/WhatToExpect";
import { VehicleTypes } from "@/components/VehicleTypes";
import { WorkshopCapabilities } from "@/components/WorkshopCapabilities";
import { GoogleReviewsBadge } from "@/components/GoogleReviewsBadge";
import { LazyWidget } from "@/components/LazyWidget";
import { RelatedBlogPosts } from "@/components/RelatedBlogPosts";
import { MOTBookingForm } from "@/components/MOTBookingForm";
import { MOTSlotAvailability } from "@/components/MOTSlotAvailability";
import { TrustSection } from "@/components/TrustSection";
import { ComplianceChecklist } from "@/components/ComplianceChecklist";
import { FindUs } from "@/components/FindUs";
import { ArrivalFAQ } from "@/components/ArrivalFAQ";
import { ComplianceBlock } from "@/components/ComplianceBlock";
import { QuickContact } from "@/components/QuickContact";
import { PrimaryCTA } from "@/components/PrimaryCTA";
import { trackEvent } from "@/lib/tracking";
import { getPublicConfig } from "@/lib/site-content.functions";
import { listPublicGalleryPhotos } from "@/lib/gallery.functions";
import { FeaturedPhotosStrip } from "@/components/FeaturedPhotosStrip";
import { CmsSections } from "@/components/CmsSections";
import { VerifiedBusinessCard } from "@/components/VerifiedBusinessCard";
import { seoHead, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { HelpfulGuidesGrid } from "@/components/HelpfulGuidesGrid";
import { DriverSupportStrip } from "@/components/DriverSupportStrip";
import { LocalTrustBar } from "@/components/LocalTrustBar";
import { WhyChooseWoodseer } from "@/components/WhyChooseWoodseer";
import { ReviewReferralBlock } from "@/components/ReviewReferralBlock";
import { MarquesStrip } from "@/components/MarquesStrip";
import { PrestigeCallout } from "@/components/PrestigeCallout";
import { PricingTransparency } from "@/components/PricingTransparency";

const homeFaqs = [
  { q: "Where are you located?", a: "We're based at 16 Pixley Street, London E14 7DF — but the workshop entrance is via the yard at 14 Copenhagen Place, E14 7DF. We serve drivers across Poplar, Canary Wharf, Isle of Dogs, Limehouse, Docklands and Tower Hamlets." },
  { q: "Do I need to book in advance?", a: "We recommend calling ahead to book your MOT or service, but we also welcome walk-ins for quick checks and enquiries." },
  { q: "What vehicles do you work on?", a: "We service and repair all makes and models including cars, vans and light commercial vehicles — from daily drivers to executive and premium motors." },
  { q: "How much does an MOT cost?", a: "Contact us for our current MOT pricing. We're competitively priced and there are no hidden fees." },
  { q: "Do you offer collection or drop-off?", a: "Please call us to discuss — we may be able to accommodate collection and delivery depending on your location." },
  { q: "Will I be told before any extra work is done?", a: "Always. We never carry out work without your approval first. You'll get a clear explanation and quote before we proceed." },
];

export const Route = createFileRoute("/")({
  loader: async () => {
    // Pull live compliance so the FAQPage + LocalBusiness JSON-LD stay in sync with admin edits.
    const [config, featured] = await Promise.all([
      getPublicConfig(),
      listPublicGalleryPhotos({ data: { featuredOnly: true, limit: 8 } }),
    ]);
    return { compliance: config.compliance, featuredPhotos: featured.photos };
  },
  head: ({ loaderData }) => {
    const c = loaderData?.compliance;
    const mergedFaqs = [
      ...homeFaqs,
      ...((c?.arrivalFaqs || []).map((f) => ({ q: f.q, a: f.a }))),
    ];

    const head = seoHead({
      path: "/",
      title: "MOT, Servicing & Car Repairs East London | Woodseer Autos",
      description:
        `Independent garage in E14. MOT (classes 1–7), servicing, diagnostics, repairs and bodywork. Honest advice, fair pricing. Call ${NAP.phone}.`,
      jsonLd: [
        localBusinessSchema(complianceToBusinessOpts(c)),
        faqSchema(mergedFaqs),
        {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://woodseerautos.co.uk/#website",
          name: "Woodseer Autos",
          url: "https://woodseerautos.co.uk",
          inLanguage: "en-GB",
          publisher: { "@id": "https://woodseerautos.co.uk/#business" },
          potentialAction: {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate: "https://woodseerautos.co.uk/blog?q={search_term_string}",
            },
            "query-input": "required name=search_term_string",
          },
        },
      ],
    });
    return {
      ...head,
      links: [
        ...head.links,
        // Preload the LCP hero image so it paints faster on the homepage.
        { rel: "preload", as: "image", href: "/images/hero-homepage.jpg", fetchPriority: "high" } as never,
      ],
    };
  },
  component: HomePage,
});

function HomePage() {
  const biz = getBusinessConfig();
  const { featuredPhotos } = Route.useLoaderData();
  const [overlay, setOverlay] = useState<{ desktop: number; mobile: number; tone: "dark" | "light" }>({
    desktop: 0.72, mobile: 0.82, tone: "dark",
  });
  useEffect(() => {
    let cancelled = false;
    import("@/lib/cms.functions").then(({ getHeroOverlay }) =>
      getHeroOverlay().then((v) => { if (!cancelled) setOverlay(v); }).catch(() => {})
    );
    return () => { cancelled = true; };
  }, []);
  const baseColor = overlay.tone === "light" ? "255 255 255" : "10 12 22";

  return (
    <>
      {/* ── HERO ── */}
      <section
        className="relative py-24 md:py-36 overflow-hidden"
        style={{
          ["--hero-op-d" as string]: String(overlay.desktop),
          ["--hero-op-m" as string]: String(overlay.mobile),
          ["--hero-base" as string]: baseColor,
        }}
      >
        <div className="absolute inset-0">
          <img
            src="/images/hero-homepage.jpg"
            alt="Woodseer Autos garage entrance and MOT testing station in Poplar, East London"
            width={1920}
            height={1080}
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover"
          />
          {/* Premium readable overlay — directional fade for legibility, with a soft top/bottom vignette. Opacity is CMS-controlled per breakpoint. */}
          <div
            className="absolute inset-0 md:hidden"
            style={{ background: `linear-gradient(180deg, rgb(var(--hero-base) / calc(var(--hero-op-m) * 0.95)) 0%, rgb(var(--hero-base) / calc(var(--hero-op-m) * 0.7)) 60%, rgb(var(--hero-base) / calc(var(--hero-op-m) * 0.4)) 100%)` }}
          />
          <div
            className="absolute inset-0 hidden md:block"
            style={{ background: `linear-gradient(100deg, rgb(var(--hero-base) / var(--hero-op-d)) 0%, rgb(var(--hero-base) / calc(var(--hero-op-d) * 0.85)) 35%, rgb(var(--hero-base) / calc(var(--hero-op-d) * 0.4)) 70%, rgb(var(--hero-base) / calc(var(--hero-op-d) * 0.1)) 100%)` }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: `linear-gradient(180deg, rgb(var(--hero-base) / 0.3) 0%, transparent 25%, transparent 75%, rgb(var(--hero-base) / 0.4) 100%)` }}
          />
          {/* Extra left-column mask to suppress any ghosted background watermark behind the headline */}
          <div
            className="absolute inset-y-0 left-0 w-full md:w-[58%] pointer-events-none"
            style={{ background: `linear-gradient(90deg, rgb(var(--hero-base) / 0.55) 0%, rgb(var(--hero-base) / 0.25) 70%, transparent 100%)` }}
          />
        </div>
        <div className="relative mx-auto max-w-6xl px-4 md:px-6">
          <div className="grid lg:grid-cols-[1fr_minmax(360px,440px)] gap-10 lg:gap-12 items-start">
            <div className="max-w-2xl">
              <p className="text-xs font-bold text-white/80 uppercase tracking-[0.25em]">
                Honest Mechanics · Fair Pricing · No Nonsense
              </p>
              <h1 className="mt-4 text-4xl md:text-[3.5rem] font-extrabold text-white tracking-tight leading-[1.06]">
                MOTs, Servicing<br />
                & Repairs —<br />
                <span className="text-primary">Under One Roof</span>
              </h1>
              <p className="mt-5 text-base text-white/85 max-w-lg leading-relaxed">
                Trusted by local drivers across E14 and East London. Quality workmanship and clear communication — from routine MOTs to complex diagnostics and bodywork.
              </p>
              <PrimaryCTA location="hero" intent="MOT or quote" variant="dark" />

              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/90">
                <span className="flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                  <span className="font-semibold text-white">{biz.address}, {biz.postcode}</span>
                </span>
                <span className="hidden sm:inline text-white/40" aria-hidden="true">·</span>
                <span>Yard entrance · 14 Copenhagen Place</span>
                <span className="hidden sm:inline text-white/40" aria-hidden="true">·</span>
                <span>Mon–Sat 9am–6pm</span>
                <span className="hidden sm:inline text-white/40" aria-hidden="true">·</span>
                <a
                  href="#find-us"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("find-us")?.scrollIntoView({ behavior: "smooth" });
                    trackEvent("directions_click", { location: "hero", source: "hero", destination: "entrance", action: "scroll_to_find_us" });
                  }}
                  className="inline-flex items-center gap-1 rounded text-primary font-bold hover:text-primary/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-foreground"
                >
                  Get directions →
                </a>
              </div>

            </div>

            {/* Prominent hero booking form — instant client-side validation, mobile-first stacking */}
            <div className="lg:pt-2">
              <MOTBookingForm trackingLocation="homepage_hero" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Social Proof Number Row ── */}
      <section className="border-b border-border/50 bg-card py-3">
        <div className="mx-auto max-w-6xl px-4 md:px-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
          <GoogleReviewsBadge compact />
          <span className="hidden sm:inline text-border">|</span>
          <span className="font-semibold">MOT Station V106376</span>
          <span className="hidden sm:inline text-border">|</span>
          <span>Classes 1–5 & 7</span>
          <span className="hidden sm:inline text-border">|</span>
          <span>Mon–Sat, 9–6</span>
        </div>
      </section>

      <LocalTrustBar />

      <ReassuranceBanner />


      {/* ── INLINE BOOKING FORM (above the fold for mobile, high-intent capture) ── */}
      <AnimatedSection>
        <section className="section-y-tight bg-secondary/40">
          <div className="mx-auto max-w-6xl px-4 md:px-6 grid lg:grid-cols-[1fr_minmax(360px,460px)] gap-10 items-center">
            <div>
              <p className="eyebrow">Book in 60 seconds</p>
              <h2 className="mt-3 display-lg text-foreground">
                Get your MOT or service booked — without the back and forth.
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed max-w-xl">
                Tell us your reg, what you need and a preferred date. We'll call back to confirm — usually within a few hours during workshop hours.
              </p>
              <ul className="mt-6 space-y-2.5 text-sm">
                {[
                  "DVSA-authorised MOT testing — classes 1, 2, 3, 4, 5 & 7",
                  "Quick turnaround where parts and workshop load allow",
                  "Workmanship warranty available on qualifying repairs",
                  "No work done without your approval",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2.5 text-foreground/75">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
            <MOTBookingForm trackingLocation="homepage_inline" />
          </div>
        </section>
      </AnimatedSection>

      {/* ── REAL-TIME MOT SLOTS ── */}
      <AnimatedSection>
        <section className="section-y-tight">
          <div className="mx-auto max-w-5xl px-4 md:px-6">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="eyebrow">Pick a time that works</p>
              <h2 className="mt-3 display-lg text-foreground">See the next available MOT slots.</h2>
              <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                Tap a slot and we'll call back to confirm. Nothing match? Request a callback and we'll find a time.
              </p>
            </div>
            <MOTSlotAvailability trackingLocation="homepage_slots" />
          </div>
        </section>
      </AnimatedSection>

      <ServicePicker />
      <MarquesStrip />
      <ProofStrip />
      <DriverSupportStrip />
      <PricingTransparency />
      <PrestigeCallout
        photos={(featuredPhotos || []).slice(0, 4).map((p) => ({
          url: p.public_url,
          alt: p.alt_text || "Premium vehicle in the Woodseer Autos workshop, Poplar E14",
        }))}
      />
      <TrustSection />
      <ComplianceChecklist />

      <HelpfulGuidesGrid />


      {/* ── WHY CHOOSE US ── */}
      <AnimatedSection>
        <section className="section-y bg-secondary/50">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <div className="max-w-2xl">
              <p className="eyebrow">Why drivers stay with us</p>
              <h2 className="mt-3 display-lg text-foreground">
                The garage your neighbour told you about.
              </h2>
              <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-xl">
                Most of our customers come from word of mouth. We try to earn that — by being straight with you, doing the job properly, and not adding work that isn't needed.
              </p>
            </div>
            <StaggerContainer className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { num: "01", title: "We'll tell you straight", desc: "If your car needs the work, we'll explain why. If it doesn't, we'll tell you that too." },
                { num: "02", title: "Plain English, every time", desc: "No mechanic-speak. We walk you through what we found and what your options are before anything starts." },
                { num: "03", title: "MOT, service, repairs, body — one place", desc: "From an MOT to a full diagnostic or a paint touch-up, you don't need to drop your car off twice." },
                { num: "04", title: "Realistic timings", desc: "We'll give you an honest read on how long the job will take and call you if anything changes." },
                { num: "05", title: "No surprise bills", desc: "Every quote is signed off by you before the spanners come out. No mystery charges at collection." },
                { num: "06", title: "Fair pricing for proper work", desc: "Itemised quotes, OE-quality parts, and prices that don't sting. Cheaper than a main dealer for the same standard." },
              ].map((item) => (
                <StaggerItem key={item.title}>
                  <div className="relative h-full rounded-2xl surface-premium surface-premium-hover p-6 md:p-7">
                    <div className="flex items-start justify-between">
                      <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-primary/80">{item.num}</span>
                      <span className="h-1 w-8 rounded-full bg-primary/30" />
                    </div>
                    <h3 className="mt-5 text-lg font-bold text-foreground tracking-tight">{item.title}</h3>
                    <p className="mt-2 text-[14px] text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      <WhyChooseWoodseer page="home" />

      {/* ── REVIEWS ── */}
      <AnimatedSection>
        <section className="section-y">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div className="max-w-xl">
                <p className="eyebrow">Customer voice</p>
                <h2 className="mt-3 display-lg text-foreground">What our customers say.</h2>
                <p className="mt-4 text-muted-foreground leading-relaxed">
                  Recent Google reviews from drivers across E14 and East London — see our profile for the latest.
                </p>
              </div>
              <GoogleReviewsBadge />
            </div>
            <StaggerContainer className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {reviews.slice(0, 6).map((r, i) => (
                <StaggerItem key={i}>
                  <ReviewCard review={r} featured={i === 0} />
                </StaggerItem>
              ))}
            </StaggerContainer>
            <div className="mt-10 text-center">
              <Link to="/reviews" className="inline-flex items-center gap-1.5 text-primary font-bold text-sm hover:text-primary/80 transition-colors">
                Read all reviews <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </AnimatedSection>

      <WhatToExpect />

      {/* ── ALL SERVICES ── */}
      <AnimatedSection>
        <section className="section-y bg-secondary/50">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="max-w-2xl">
              <p className="eyebrow">Full capability</p>
              <h2 className="mt-3 display-lg text-foreground">Every service your vehicle needs — under one roof.</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed max-w-xl">
                From a routine MOT to complex diagnostics or a full bodywork repair, the same workshop and the same standards handle the lot.
              </p>
            </div>
            <StaggerContainer className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {services.map((s) => (
                <StaggerItem key={s.slug}>
                  <ServiceCard service={s} />
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      <VehicleTypes />

      <FeaturedPhotosStrip photos={featuredPhotos} />

      <WorkshopCapabilities />

      {/* ── AREAS ── */}
      <AnimatedSection>
        <section className="section-y bg-secondary/50">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="max-w-2xl">
              <p className="eyebrow">Local coverage</p>
              <h2 className="mt-3 display-lg text-foreground">Trusted by drivers across East London.</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed max-w-xl">
                We're a short drive from every corner of E14, E1 and the surrounding postcodes. Tap your area for tailored arrival info, parking and directions to the yard.
              </p>
            </div>
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {areas.map((a) => (
                <Link
                  key={a.slug}
                  to="/areas/$area"
                  params={{ area: a.slug }}
                  className="group relative flex flex-col rounded-2xl surface-premium surface-premium-hover p-5"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/12 to-primary/4 border border-primary/15 text-primary group-hover:from-primary group-hover:to-primary group-hover:text-primary-foreground group-hover:border-primary transition-all">
                      <MapPin className="h-4 w-4" />
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                  </div>
                  <span className="mt-5 text-sm font-bold text-foreground tracking-tight">{a.name}</span>
                  <span className="mt-0.5 text-[11px] text-muted-foreground">MOT · servicing · repairs</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      <FindUs />

      <ArrivalFAQ />

      <AnimatedSection>
        <FAQAccordion faqs={homeFaqs} />
      </AnimatedSection>

      <RelatedBlogPosts limit={3} />

      <LazyWidget
        name="InstagramFeed"
        loader={() => import("@/components/InstagramFeed")}
        exportName="InstagramFeed"
        timeoutMs={5000}
        deferMs={1500}
      />

      <ComplianceBlock />

      <VerifiedBusinessCard />

      {/* Editable CMS sections (admin → "CMS: Page Sections" → page = home) */}
      <CmsSections pageSlug="home" />

      {/* Direct contact hub — Linktree + QR + every contact channel */}
      <AnimatedSection>
        <section className="section-y bg-secondary/40">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <QuickContact location="home_final" />
          </div>
        </section>
      </AnimatedSection>

      <CTABlock
        headline="Get Your Vehicle Sorted Today"
        subtext="Whether it's an MOT, a service or a repair — give us a call and we'll take care of it. No pressure, just honest advice."
      />
    </>
  );
}
